import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { ArrowLeft, Save, Trash2, Eye, Check } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const QuestionEdit = () => {
  const { id } = useParams()
  const [formData, setFormData] = useState({
    question: '',
    option1: '',
    option2: '',
    option3: '',
    option4: '',
    correctAnswer: '',
    category: '',
    difficulty: 'medium',
    explanation: ''
  })
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showPreview, setShowPreview] = useState(true)
  const [questionData, setQuestionData] = useState(null)
  
  const { getQuestion, updateQuestion, deleteQuestion, getCategories } = useFirebase()
  const navigate = useNavigate()

  useEffect(() => {
    loadData()
  }, [id])

  const loadData = async () => {
    try {
      const [question, categoriesData] = await Promise.all([
        getQuestion(id),
        getCategories()
      ])
      
      if (!question) {
        toast.error('Question not found')
        navigate('/questions')
        return
      }
      
      setQuestionData(question)
      setCategories(categoriesData)
      
      // Populate form
      setFormData({
        question: question.question || '',
        option1: question.options?.[0] || '',
        option2: question.options?.[1] || '',
        option3: question.options?.[2] || '',
        option4: question.options?.[3] || '',
        correctAnswer: question.correctAnswer?.toString() || '',
        category: question.category || '',
        difficulty: question.difficulty || 'medium',
        explanation: question.explanation || ''
      })
    } catch (error) {
      console.error('Error loading question:', error)
      toast.error('Failed to load question')
      navigate('/questions')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.question.trim()) {
      toast.error('Please enter a question')
      return
    }
    
    if (!formData.option1.trim() || !formData.option2.trim() || 
        !formData.option3.trim() || !formData.option4.trim()) {
      toast.error('Please fill in all four options')
      return
    }
    
    if (formData.correctAnswer === '') {
      toast.error('Please select the correct answer')
      return
    }

    setSaving(true)
    
    try {
      const updatedData = {
        question: formData.question.trim(),
        options: [
          formData.option1.trim(),
          formData.option2.trim(),
          formData.option3.trim(),
          formData.option4.trim()
        ],
        correctAnswer: parseInt(formData.correctAnswer),
        category: formData.category || '',
        difficulty: formData.difficulty,
        explanation: formData.explanation.trim()
      }
      
      await updateQuestion(id, updatedData)
      toast.success('Question updated successfully')
      navigate('/questions')
    } catch (error) {
      console.error('Error updating question:', error)
      toast.error('Failed to update question')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this question? This action cannot be undone.')) {
      try {
        await deleteQuestion(id)
        toast.success('Question deleted successfully')
        navigate('/questions')
      } catch (error) {
        console.error('Error deleting question:', error)
        toast.error('Failed to delete question')
      }
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  const isPreviewReady = formData.question && formData.option1 && formData.option2 && 
                        formData.option3 && formData.option4

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Question</h1>
          <p className="text-sm text-gray-600">
            Update the exam question details
          </p>
        </div>
        <Link to="/questions" className="btn-outline">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Questions
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Question Details</h3>
          </div>
          
          <div className="card-body">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Question Text */}
              <div>
                <label htmlFor="question" className="form-label">
                  Question Text *
                </label>
                <textarea
                  id="question"
                  name="question"
                  rows={4}
                  className="form-textarea"
                  placeholder="Enter the question text..."
                  value={formData.question}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="option1" className="form-label">
                    Option A *
                  </label>
                  <input
                    type="text"
                    id="option1"
                    name="option1"
                    className="form-input"
                    placeholder="Enter option A"
                    value={formData.option1}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="option2" className="form-label">
                    Option B *
                  </label>
                  <input
                    type="text"
                    id="option2"
                    name="option2"
                    className="form-input"
                    placeholder="Enter option B"
                    value={formData.option2}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="option3" className="form-label">
                    Option C *
                  </label>
                  <input
                    type="text"
                    id="option3"
                    name="option3"
                    className="form-input"
                    placeholder="Enter option C"
                    value={formData.option3}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="option4" className="form-label">
                    Option D *
                  </label>
                  <input
                    type="text"
                    id="option4"
                    name="option4"
                    className="form-input"
                    placeholder="Enter option D"
                    value={formData.option4}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              {/* Correct Answer */}
              <div>
                <label htmlFor="correctAnswer" className="form-label">
                  Correct Answer *
                </label>
                <select
                  id="correctAnswer"
                  name="correctAnswer"
                  className="form-select"
                  value={formData.correctAnswer}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select the correct answer</option>
                  <option value="0">Option A</option>
                  <option value="1">Option B</option>
                  <option value="2">Option C</option>
                  <option value="3">Option D</option>
                </select>
              </div>

              {/* Category and Difficulty */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="category" className="form-label">
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    className="form-select"
                    value={formData.category}
                    onChange={handleChange}
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="difficulty" className="form-label">
                    Difficulty Level
                  </label>
                  <select
                    id="difficulty"
                    name="difficulty"
                    className="form-select"
                    value={formData.difficulty}
                    onChange={handleChange}
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              {/* Explanation */}
              <div>
                <label htmlFor="explanation" className="form-label">
                  Explanation (Optional)
                </label>
                <textarea
                  id="explanation"
                  name="explanation"
                  rows={3}
                  className="form-textarea"
                  placeholder="Provide an explanation for the correct answer..."
                  value={formData.explanation}
                  onChange={handleChange}
                />
              </div>

              {/* Submit Buttons */}
              <div className="flex space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? (
                    <div className="flex items-center">
                      <div className="loading-spinner h-4 w-4 mr-2"></div>
                      Updating...
                    </div>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Update Question
                    </>
                  )}
                </button>
                <Link to="/questions" className="btn-outline">
                  Cancel
                </Link>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="btn-danger"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Question
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-6">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Question Preview
                </h3>
                <button
                  onClick={() => setShowPreview(!showPreview)}
                  className="btn-outline text-sm"
                >
                  <Eye className="h-4 w-4 mr-1" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </button>
              </div>
            </div>
            
            <div className="card-body">
              {isPreviewReady && showPreview ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">
                      {formData.question}
                    </h4>
                    <div className="space-y-2">
                      {['A', 'B', 'C', 'D'].map((label, index) => {
                        const option = formData[`option${index + 1}`]
                        const isCorrect = parseInt(formData.correctAnswer) === index
                        
                        return (
                          <div
                            key={label}
                            className={`flex items-center space-x-3 p-3 rounded-md ${
                              isCorrect 
                                ? 'bg-success-50 border border-success-200' 
                                : 'bg-gray-50'
                            }`}
                          >
                            <span className="font-medium text-gray-700">
                              {label}.
                            </span>
                            <span className="text-gray-900">{option}</span>
                            {isCorrect && (
                              <Check className="h-4 w-4 text-success-600 ml-auto" />
                            )}
                          </div>
                        )
                      })}
                    </div>
                    
                    {formData.explanation && (
                      <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <h5 className="font-medium text-blue-900 mb-1">
                          Explanation:
                        </h5>
                        <p className="text-blue-800 text-sm">
                          {formData.explanation}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Eye className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {isPreviewReady 
                      ? 'Click "Show Preview" to see how your question will look'
                      : 'Fill in the question and options to see a preview'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Question History */}
          {questionData && (questionData.createdAt || questionData.updatedAt) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Question History</h3>
              </div>
              <div className="card-body">
                <div className="space-y-2 text-sm text-gray-600">
                  {questionData.createdAt && (
                    <p>
                      <span className="font-medium">Created:</span> 
                      {' '}{new Date(questionData.createdAt).toLocaleString()}
                      {questionData.createdBy && (
                        <span> by {questionData.createdBy}</span>
                      )}
                    </p>
                  )}
                  {questionData.updatedAt && (
                    <p>
                      <span className="font-medium">Last Updated:</span> 
                      {' '}{new Date(questionData.updatedAt).toLocaleString()}
                      {questionData.updatedBy && (
                        <span> by {questionData.updatedBy}</span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuestionEdit
